/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRLegalRouting,
  SLRProtectedRoute,
  SLRSpinner,
  useAuth
} from '@SLR/shared-library';
import {
  ADMIN_ONBOARDING_REQUEST_PATH,
  ADMIN_ONBOARDING_REQUESTS_PATH,
  CHOOSE_ORGANIZATION_PATH,
  DELETED_PROFILE_PATH,
  INVITATION_CHECK,
  INVITATION_HANDLE,
  legalRouts,
  ONBOARDING_PATH,
  ORGANIZATION_DETAILS_PATH,
  ORGANIZATIONS_PATH,
  PROFILE_DATA_PRIVACY_PATH,
  PROFILE_EMAIL_UPDATE_PATH,
  PROFILE_PASSWORD_UPDATE_PATH,
  PROFILE_PATH,
  PROFILE_SECURITY_PATH,
  SOLUTIONS_PATH
} from 'configs';
import { FC, useCallback } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { ChooseOrganizationPage } from './choose-organization';
import { InvitationCheckPage, InvitationHandlePage } from './invitations';
import { DraftPage } from './onboarding/draft/DraftPage';
import { RequestPage } from './onboarding/request/RequestPage';
import { RequestsPage } from './onboarding/request/RequestsPage';
import { OrganizationDetails, OrganizationsPage } from './organizations';
import {
  DeletedProfile,
  ProfileDataPrivacy,
  ProfileDetails,
  ProfileEmailChange,
  ProfilePage,
  ProfilePasswordChange,
  ProfileSecurity
} from './profile';
import { SolutionsPage } from './solutions/SolutionsPage';

export const UserPortalRouting: FC = () => {
  const { pathname } = useLocation();

  const { isAuthenticated, login, register, hasClientRole } = useAuth();

  const handleAuthentication = useCallback(() => {
    if (pathname.includes(INVITATION_HANDLE))
      return register(window.location.href);

    return login(window.location.href);
  }, [login, pathname, register]);

  const isOnboardingAdmin = hasClientRole('onboarding', 'mdl');

  return (
    <Routes>
      <Route
        element={
          <SLRProtectedRoute
            condition={isAuthenticated}
            callback={handleAuthentication}
            callbackSpinner={<SLRSpinner />}
          />
        }
      >
        <Route index element={<Navigate to={PROFILE_PATH} />} />
        <Route
          path={PROFILE_PATH}
          element={
            <ProfilePage>
              <ProfileDetails />
            </ProfilePage>
          }
        />

        <Route
          path={PROFILE_EMAIL_UPDATE_PATH}
          element={
            <ProfilePage>
              <ProfileEmailChange />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_PASSWORD_UPDATE_PATH}
          element={
            <ProfilePage>
              <ProfilePasswordChange />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_SECURITY_PATH}
          element={
            <ProfilePage>
              <ProfileSecurity />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_DATA_PRIVACY_PATH}
          element={
            <ProfilePage>
              <ProfileDataPrivacy />
            </ProfilePage>
          }
        />
        <Route path={ORGANIZATIONS_PATH} element={<OrganizationsPage />} />
        <Route
          path={ORGANIZATION_DETAILS_PATH}
          element={<OrganizationDetails />}
        />
        <Route
          path={CHOOSE_ORGANIZATION_PATH}
          element={<ChooseOrganizationPage />}
        />
        <Route path={INVITATION_HANDLE} element={<InvitationHandlePage />} />

        <Route path={SOLUTIONS_PATH} element={<SolutionsPage />} />

        <Route element={<SLRProtectedRoute condition={isOnboardingAdmin} />}>
          <Route
            path={ADMIN_ONBOARDING_REQUESTS_PATH}
            element={<RequestsPage />}
          />
          <Route
            path={ADMIN_ONBOARDING_REQUEST_PATH}
            element={<RequestPage />}
          />
        </Route>
      </Route>

      <Route path={INVITATION_CHECK} element={<InvitationCheckPage />} />
      <Route path={DELETED_PROFILE_PATH} element={<DeletedProfile />} />

      <Route element={<SLRProtectedRoute condition={!isAuthenticated} />}>
        <Route path={ONBOARDING_PATH} element={<DraftPage />} />
      </Route>

      <Route
        path="*"
        element={
          <SLRLegalRouting legals={legalRouts} redirect={PROFILE_PATH} />
        }
      />
    </Routes>
  );
};
